/**
 * Google Analytics 4 provider.
 *
 * Loads the gtag.js snippet dynamically and provides a thin wrapper
 * for sending events. The measurement ID is read from
 * REACT_APP_GA4_MEASUREMENT_ID at build time.
 */

const MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';

let initialised = false;

/**
 * Inject the gtag.js script and initialise the GA4 data stream.
 * No-ops if the measurement ID is not configured.
 */
export function initGA4(): void {
  if (initialised || !MEASUREMENT_ID) {
    return;
  }

  try {
    // Global dataLayer used by gtag.js
    const w = window as unknown as Window & {
      dataLayer: unknown[];
      gtag: (...args: unknown[]) => void;
    };
    w.dataLayer = w.dataLayer || [];

    w.gtag = w.gtag || function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer.push(arguments);
    };
    w.gtag('js', new Date());
    w.gtag('config', MEASUREMENT_ID, { send_page_view: false });

    // Dynamically insert the gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.appendChild(script);

    initialised = true;
  } catch (error) {
    console.error('Failed to initialise GA4:', error);
  }
}

/**
 * Send an event to GA4.
 */
export function trackGA4Event(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (!initialised) return;

  try {
    const w = window as unknown as Window & {
      gtag: (...args: unknown[]) => void;
    };
    if (typeof w.gtag === 'function') {
      w.gtag('event', eventName, params);
    }
  } catch {
    // Silently ignore tracking errors
  }
}
